import { z } from "zod";

/** Public template surface: cover/guts template files and product metadata for any published product. */

export type TemplateArtifact = "cover" | "pages";

const metadataSchema = z.object({
  specification: z.object({
    pages_spec: z.object({
      min_page_count: z.number().int().positive(),
    }),
  }),
});

/** The subset of the public template-metadata envelope the example consumes. */
export type TemplateMetadata = z.infer<typeof metadataSchema>;

export function normalizeBaseUrl(base: string): string {
  return base.replace(/\/+$/, "");
}

/** The configured template base URL, or undefined when the env var is unset. */
export function templateApiBaseUrl(): string | undefined {
  const raw = import.meta.env.VITE_PUBLIC_TEMPLATE_ENDPOINT_URL;
  return raw ? normalizeBaseUrl(raw) : undefined;
}

/** The footer label for a product: its name, falling back to its description when name is blank. */
export function resolveProductLabel(name?: string | null, description?: string | null): string {
  return name?.trim() || description?.trim() || "";
}

export function buildTemplateUrl(
  base: string,
  manufacturingSku: string,
  artifact: TemplateArtifact,
  params: { pageCount: number; productDescription: string }
): string {
  const url = new URL(`${base}/published/${encodeURIComponent(manufacturingSku)}/${artifact}/pdf`);
  url.searchParams.set("pageCount", String(params.pageCount));
  if (params.productDescription) {
    url.searchParams.set("productDescription", params.productDescription);
  }
  return url.toString();
}

/** Composes the caller-supplied footer description: the product label plus its page count. */
export function composeProductDescription(label: string, pageCount: number): string {
  const pages = `${pageCount} ${pageCount === 1 ? "page" : "pages"}`;
  const trimmed = label.trim();
  return trimmed ? `${trimmed} — ${pages}` : pages;
}

export function minPageCountFromMetadata(metadata: TemplateMetadata): number {
  return metadata.specification.pages_spec.min_page_count;
}

export interface OrderFiles {
  coverUrl: string;
  gutsUrl: string;
  productDescription: string;
}

/** Derives the cover and guts URLs and footer description for a product from its metadata. */
export function deriveOrderFiles(input: {
  base: string;
  manufacturingSku: string;
  label: string;
  metadata: TemplateMetadata;
}): OrderFiles {
  const pageCount = minPageCountFromMetadata(input.metadata);
  const productDescription = composeProductDescription(input.label, pageCount);
  return {
    productDescription,
    coverUrl: buildTemplateUrl(input.base, input.manufacturingSku, "cover", { pageCount, productDescription }),
    gutsUrl: buildTemplateUrl(input.base, input.manufacturingSku, "pages", { pageCount, productDescription }),
  };
}

/** Fetches and validates the by-SKU template metadata from the unauthenticated public surface. */
export async function fetchTemplateMetadata(base: string, manufacturingSku: string): Promise<TemplateMetadata> {
  const response = await fetch(`${base}/published/${encodeURIComponent(manufacturingSku)}/metadata`);
  if (!response.ok) {
    throw new Error(`Template metadata request failed (${response.status})`);
  }
  return metadataSchema.parse(await response.json());
}
